import axios from 'axios';
import '../config.js';

export const ROLES = Object.freeze({
  AdminLevelUser: 'AdminLevelUser',
  TopLevelUser: 'TopLevelUser',
  AppProviderLevelUser: 'AppProviderLevelUser',
  CustomerLevelUser: 'CustomerLevelUser',
  LocationLevelUser: 'LocationLevelUser',
  SeniorLocalUser: 'SeniorLocalUser',
  DutyManager: 'DutyManager',
  JuniorLocalUser: 'JuniorLocalUser',
  KonkrUser: 'KonkrUser',
});

export const FEATURE_FLAGS = Object.freeze({
  NotificationSettings: 'notification-settings',
  ManageMembers: 'managemembers',
  TileTimeLimitation: 'tile-time-limitation',
});

const ACCESS_CONTEXT_KEY = 'accessContext';

const toLower = (value) => String(value || '').trim().toLowerCase();

const normalizeFeatureFlagName = (value) => toLower(value).replace(/\s+/g, '-');

const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return [];
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

const firstNonNullish = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined) return value;
  }
  return undefined;
};

const getValueAnyKey = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  }

  const normalizedMap = new Map(
    Object.keys(obj).map((k) => [
      String(k)
        .replace(/[^a-zA-Z0-9]/g, '')
        .toLowerCase(),
      obj[k],
    ])
  );

  for (const key of keys) {
    const normalizedKey = String(key)
      .replace(/[^a-zA-Z0-9]/g, '')
      .toLowerCase();
    if (normalizedMap.has(normalizedKey)) return normalizedMap.get(normalizedKey);
  }

  return undefined;
};

const normalizeRole = (rawRole) => {
  const role = toLower(rawRole);
  if (role === toLower(ROLES.KonkrUser)) return ROLES.KonkrUser;
  const siteRole = [ROLES.SeniorLocalUser, ROLES.DutyManager, ROLES.JuniorLocalUser]
    .find(value => toLower(value) === role);
  if (siteRole) return siteRole;
  if (
    role === toLower(ROLES.AdminLevelUser) ||
    role === toLower(ROLES.TopLevelUser) ||
    role === 'adminuser' ||
    role === 'admin' ||
    role === 'topleveluser' ||
    role === 'toplevel'
  ) {
    return ROLES.AdminLevelUser;
  }
  if (
    role === toLower(ROLES.AppProviderLevelUser) ||
    role === 'appprovideruser' ||
    role === 'appprovider'
  ) {
    return ROLES.AppProviderLevelUser;
  }
  if (
    role === toLower(ROLES.CustomerLevelUser) ||
    role === 'customeruser' ||
    role === 'customer'
  ) {
    return ROLES.CustomerLevelUser;
  }
  if (
    role === toLower(ROLES.LocationLevelUser) ||
    role === 'locationuser' ||
    role === 'location'
  ) {
    return ROLES.LocationLevelUser;
  }
  return null;
};

const firstValidRole = (roles) => {
  for (const rawRole of roles) {
    const next = normalizeRole(rawRole);
    if (next) return next;
  }
  return null;
};

const getClaims = (profile) => {
  const explicitClaims = profile && typeof profile.claims === 'object' ? profile.claims : {};
  return {
    ...explicitClaims,
    customerId: firstNonNullish(
      getValueAnyKey(profile, ['customerId', 'customerid', 'CustomerId']),
      getValueAnyKey(explicitClaims, ['customerId', 'customerid', 'CustomerId'])
    ),
    appProviderId: firstNonNullish(
      getValueAnyKey(profile, ['appProviderId', 'appproviderid', 'AppProviderId']),
      getValueAnyKey(explicitClaims, ['appProviderId', 'appproviderid', 'AppProviderId'])
    ),
    locationId: firstNonNullish(
      getValueAnyKey(profile, ['locationId', 'locationid', 'LocationId']),
      getValueAnyKey(explicitClaims, ['locationId', 'locationid', 'LocationId'])
    ),
    locationName: firstNonNullish(
      getValueAnyKey(profile, ['locationName', 'locationname', 'LocationName']),
      getValueAnyKey(explicitClaims, ['locationName', 'locationname', 'LocationName'])
    ),
  };
};

const getRawRoles = (profile, claims) => {
  const directRole = getValueAnyKey(profile, ['role', 'Role']);
  const directRoles = getValueAnyKey(profile, ['roles', 'Roles']);
  const claimRole = getValueAnyKey(claims, ['role', 'roles', 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role']);

  return [...toArray(directRoles), ...toArray(directRole), ...toArray(claimRole)];
};

const deriveLegacyContext = (profile) => {
  const key = String(profile && profile.key ? profile.key : '');
  const value = profile ? profile.value : null;
  const lower = key.toLowerCase();
  const parts = key.split(':');
  const pathPart = parts[0] || '';
  const appProviderIdFromKey = parts.length > 1 ? toNumberOrNull(parts[1]) : null;

  if (lower === 'customers') {
    return {
      role: ROLES.AdminLevelUser,
      claims: {
        customerId: null,
        appProviderId: appProviderIdFromKey,
        locationId: null,
      },
      legacyPath: '/customers',
    };
  }

  if (lower === 'appprovider') {
    return {
      role: ROLES.AppProviderLevelUser,
      claims: {
        customerId: null,
        appProviderId: toNumberOrNull(value),
        locationId: null,
      },
      legacyPath: '/support/customers',
    };
  }

  if (lower.startsWith('support/sites')) {
    return {
      role: ROLES.CustomerLevelUser,
      claims: {
        customerId: toNumberOrNull(value),
        appProviderId: appProviderIdFromKey,
        locationId: null,
      },
      legacyPath: '/support/sites',
    };
  }

  if (lower.startsWith('support/clients')) {
    return {
      role: ROLES.LocationLevelUser,
      claims: {
        customerId: null,
        appProviderId: appProviderIdFromKey,
        locationId: toNumberOrNull(value),
      },
      legacyPath: '/support/clients',
    };
  }

  return {
    role: null,
    claims: {
      customerId: toNumberOrNull(value),
      appProviderId: appProviderIdFromKey,
      locationId: null,
    },
    legacyPath: pathPart ? `/${pathPart.replace(/^\//, '')}` : '/login',
  };
};

const getDefaultPathForRole = (role) => {
  switch (role) {
    case ROLES.AdminLevelUser:
    case ROLES.TopLevelUser:
      return '/customers';
    case ROLES.AppProviderLevelUser:
      return '/support/customers';
    case ROLES.CustomerLevelUser:
      return '/support/sites';
    case ROLES.LocationLevelUser:
      return '/support/tiles';
    case ROLES.SeniorLocalUser:
    case ROLES.DutyManager:
    case ROLES.JuniorLocalUser:
      return '/support/site-viewer';
    case ROLES.KonkrUser:
      return '/support/konkr/advertisers';
    default:
      return '/login';
  }
};

const normalizeLandingPath = (value) => {
  const asString = String(value || '').trim();
  if (!asString.startsWith('/')) return null;
  return asString;
};

export const normalizeAccessContext = (profile) => {
  const claims = getClaims(profile || {});
  const rawRoles = getRawRoles(profile || {}, claims);
  const explicitRole = firstValidRole(rawRoles);
  const legacy = deriveLegacyContext(profile || {});

  const role = explicitRole || legacy.role;

  const featureFlags = Array.from(
    new Set(
      toArray(getValueAnyKey(profile, ['featureFlags']))
        .map((x) => String(x || '').trim())
        .filter(Boolean)
    )
  );

  const normalized = {
    role,
    claims: {
      customerId: firstNonNullish(toNumberOrNull(claims.customerId), legacy.claims.customerId, null),
      appProviderId: firstNonNullish(toNumberOrNull(claims.appProviderId), legacy.claims.appProviderId, null),
      locationId: firstNonNullish(toNumberOrNull(claims.locationId), legacy.claims.locationId, null),
      locationName: claims.locationName ? String(claims.locationName) : null,
    },
    featureFlags,
    notificationSettingsTester: Boolean(getValueAnyKey(profile, ['notificationSettingsTester'])),
    landingPath: normalizeLandingPath(getValueAnyKey(profile, ['landingPath', 'path', 'redirectPath', 'returnPath'])),
    legacyPath: legacy.legacyPath,
    rawProfile: profile || {},
  };

  return normalized;
};

const setCompatibilityStorage = (context, email) => {
  localStorage.removeItem('id');
  localStorage.removeItem('appid');
  localStorage.removeItem('locationid');
  localStorage.removeItem('supportsite');
  localStorage.removeItem('supportcustomer');

  if (email) {
    localStorage.setItem('userEmail', String(email));
  }

  if (!context || !context.claims) return;

  if (context.claims.appProviderId !== null && context.claims.appProviderId !== undefined) {
    localStorage.setItem('appid', String(context.claims.appProviderId));
  }

  if (context.claims.customerId !== null && context.claims.customerId !== undefined) {
    localStorage.setItem('id', String(context.claims.customerId));
  }

  if (context.claims.locationId !== null && context.claims.locationId !== undefined) {
    localStorage.setItem('locationid', String(context.claims.locationId));
  }

  if (context.claims.locationName) {
    localStorage.setItem('supportsite', String(context.claims.locationName));
  }
};

export const persistAccessContext = (context, email) => {
  localStorage.setItem(ACCESS_CONTEXT_KEY, JSON.stringify(context));
  setCompatibilityStorage(context, email);
};

export const clearAccessContextStorage = () => {
  localStorage.removeItem(ACCESS_CONTEXT_KEY);
  localStorage.removeItem('id');
  localStorage.removeItem('appid');
  localStorage.removeItem('locationid');
  localStorage.removeItem('supportsite');
  localStorage.removeItem('supportcustomer');
  localStorage.removeItem('userEmail');
};

export const getStoredAccessContext = () => {
  const raw = localStorage.getItem(ACCESS_CONTEXT_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
};

export const hasFeature = (featureName, aliases = []) => (context) => {
  if (!context) return false;

  const requested = [featureName, ...toArray(aliases)]
    .map((value) => normalizeFeatureFlagName(value))
    .filter(Boolean);

  if (requested.length === 0) return false;

  const enabledFlags = new Set(
    toArray(context.featureFlags)
      .map((value) => normalizeFeatureFlagName(value))
      .filter(Boolean)
  );

  return requested.some((value) => enabledFlags.has(value));
};

export const canAccessNotificationSettings = (context) => {
  if (!context) return false;
  if (context.notificationSettingsTester) return true;

  return hasFeature(FEATURE_FLAGS.NotificationSettings, ['notificationsettings'])(context);
};

export const canAccessMembersManagement = hasFeature(FEATURE_FLAGS.ManageMembers);

export const canAccessTileTimeLimitation = hasFeature(FEATURE_FLAGS.TileTimeLimitation);

export const canAccessSupportSettings = (context) => {
  return canAccessNotificationSettings(context) || canAccessTileTimeLimitation(context);
};

export const canAccessAdmin = (context) => {
  if (!context) return false;
  return context.role === ROLES.AdminLevelUser;
};

export const canAccessKonkr = context => Boolean(context && (
  context.role === ROLES.KonkrUser ||
  getRawRoles(context.rawProfile || {}, getClaims(context.rawProfile || {}))
    .some(role => normalizeRole(role) === ROLES.KonkrUser)
));

export const canAccessCustomerWalletBranding = (context) => {
  return Boolean(context && context.role === ROLES.CustomerLevelUser);
};

export const canAccessLocationWalletPortal = (context) => {
  return Boolean(context && context.role === ROLES.LocationLevelUser);
};

export const canAccessAuditLogs = (context) => {
  if (!context) return false;
  return context.role === ROLES.AdminLevelUser || context.role === ROLES.TopLevelUser;
};

export const canAccessInternalPages = (context) => {
	if (!context) return false;
	return context.role === ROLES.AdminLevelUser || context.role === ROLES.TopLevelUser;
};

export const resolvePostLoginPath = (context) => {
  if (!context) return '/login';
  if (context.role === ROLES.KonkrUser &&
      (!context.landingPath || ['/', '/login'].includes(context.landingPath))) return '/support/konkr/advertisers';
  if ([ROLES.SeniorLocalUser, ROLES.DutyManager, ROLES.JuniorLocalUser].includes(context.role) &&
      (!context.landingPath || context.landingPath === '/')) return '/support/site-viewer';
  if (context.landingPath) return context.landingPath;
  if (context.legacyPath) return context.legacyPath;
  if (context.role) return getDefaultPathForRole(context.role);
  return '/login';
};

export const fetchProfile = async (tokenOverride) => {
  const token = tokenOverride || localStorage.getItem('token');

  const headers = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await axios.get(global.config.url + '/auth/profile', headers);
    return response.data;
  } catch (error) {
    // Backward compatibility: older backends used /Auth/profile.
    const response = await axios.get(global.config.url + '/Auth/profile', headers);
    return response.data;
  }
};

export const fetchAndPersistAccessContext = async (tokenOverride, email) => {
  const profile = await fetchProfile(tokenOverride);
  const context = normalizeAccessContext(profile);
  persistAccessContext(context, email);
  return context;
};
